import {
  BarChart3Icon,
  BotIcon,
  BriefcaseBusinessIcon,
  CheckCheckIcon,
  FileTextIcon,
  FolderKanbanIcon,
  HandshakeIcon,
  HouseIcon,
  LifeBuoyIcon,
  PoundSterlingIcon,
  ShieldIcon,
  TicketIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import type { Me } from "@/api/me";
import { hasPermission } from "@/auth/permissions";

export interface NavSection {
  /** URL segment: /{workspace}/{slug} */
  slug: string;
  label: string;
  description?: string;
  /** Delivery phase that builds this screen when it is not a plain entity list (plan §8). */
  phase?: number;
  /** Table whose Read level decides whether the section is shown (none = hidden). */
  entity?: string;
  /** Permission code that decides visibility for screens that aren't a table. */
  permission?: string;
}

export interface Workspace {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Delivery phase for the workspace's bespoke screens (plan §8). */
  phase?: number;
  /** Shown when at least one section is (Home always is). */
  sections: NavSection[];
}

/** Staff workspaces (plan §5). Order is the left-rail order. */
export const WORKSPACES: Workspace[] = [
  { id: "home", label: "Home", icon: HouseIcon, sections: [] },
  {
    id: "delivery",
    label: "Delivery",
    icon: FolderKanbanIcon,
    phase: 2,
    sections: [
      { slug: "projects", label: "Projects", entity: "project", description: "All client projects, their milestones and financial position." },
      { slug: "board", label: "Board", entity: "project_task", description: "Tasks as a Kanban, grouped by status, sprint, assignee or priority. Drag a card to update it." },
      { slug: "sprints", label: "Sprint planning", entity: "sprint", description: "The backlog and current sprints side by side. Drag tasks between sprints." },
      { slug: "delivery-milestones", label: "Delivery milestones", entity: "delivery_milestone", description: "Delivery milestones: the stages a project is delivered and invoiced in, each grouping project milestones." },
      { slug: "milestones", label: "Project milestones", entity: "project_milestone", description: "Project milestones (within a delivery milestone) and their tasks." },
      { slug: "tasks", label: "Tasks", entity: "project_task", description: "Every task across projects." },
      { slug: "daily-activity", label: "Daily activity", entity: "daily_activity", description: "Your weekly timesheet. Log hours against tasks, tickets and other activities." },
      { slug: "tm-timesheets", label: "T&M timesheets", entity: "tm_timesheet", description: "Time & materials timesheets and approval packs (DocuSign status)." },
      { slug: "allocations", label: "Resource allocation", entity: "timesheet_resource_allocation", description: "Who is allocated to what, and when." },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: LifeBuoyIcon,
    phase: 2,
    sections: [
      { slug: "tickets", label: "Tickets", entity: "ticket", description: "Client support tickets." },
      { slug: "bau-solutions", label: "BAU solutions", entity: "bau_solution", description: "Business-as-usual solutions under support." },
      { slug: "artifacts", label: "Project artifacts", entity: "project_artifact", description: "Documentation and deliverables per project." },
      { slug: "support-models", label: "Support models", entity: "support_model", description: "Support models and their financials." },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    icon: PoundSterlingIcon,
    phase: 4,
    sections: [
      { slug: "invoices", label: "Invoices", entity: "invoice", description: "Draft, issued and paid invoices." },
      { slug: "credit-notes", label: "Credit notes", entity: "credit_note", description: "Credits raised against sent invoices." },
      { slug: "payments", label: "Payments", entity: "invoice_payment_allocation", description: "Payments received and their allocations." },
      { slug: "write-offs", label: "Write-offs", entity: "invoice_write_off" },
      { slug: "starling", label: "Starling transactions", entity: "starling_transaction", description: "Bank feed with attachments, categories and HubDoc status." },
      { slug: "bank-accounts", label: "Bank accounts", entity: "bank_account" },
      { slug: "cash-flow-mapping", label: "Cash-flow mapping", entity: "cashflow_category" },
      { slug: "fx-rates", label: "FX rates", entity: "monthly_exchange_rate", description: "HMRC monthly exchange rates." },
    ],
  },
  {
    id: "crm",
    label: "CRM",
    icon: HandshakeIcon,
    phase: 3,
    sections: [
      { slug: "leads", label: "Leads", entity: "lead", description: "The lead funnel. Drag cards to change status; flags mark next actions due within 5 days." },
      { slug: "clients", label: "Clients", entity: "client" },
      { slug: "departments", label: "Departments", entity: "department" },
      { slug: "contacts", label: "Contacts", entity: "contact" },
      { slug: "campaigns", label: "Campaigns", entity: "campaign" },
      { slug: "partnerships", label: "Partnerships", entity: "partnership" },
      { slug: "marketplace-leads", label: "Marketplace leads", entity: "marketplace_lead" },
    ],
  },
  {
    id: "people",
    label: "People",
    icon: UsersIcon,
    phase: 3,
    sections: [
      { slug: "holidays", label: "Holiday requests", entity: "holiday_request", description: "Team calendar of time off, with who is off today." },
      { slug: "bank-holidays", label: "Bank holidays", entity: "bank_holiday" },
      { slug: "holiday-types", label: "Holiday types", entity: "holiday_type" },
      { slug: "resources", label: "Resources", entity: "resource" },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3Icon,
    phase: 5,
    sections: [
      { slug: "dashboards", label: "Dashboards", permission: "reports.dashboard:view", description: "Native, real-time operational dashboards." },
    ],
  },
  {
    id: "claude",
    label: "Claude",
    icon: BotIcon,
    sections: [
      { slug: "guides", label: "Guides", entity: "claude_guide", description: "What Claude knows about Pulse and how Datanomy works, through the Pulse connector and plugin." },
      { slug: "skills", label: "Skills", entity: "claude_skill", description: "Step-by-step workflows Claude follows; slash commands in the Pulse plugin." },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: ShieldIcon,
    phase: 5,
    sections: [
      { slug: "users", label: "Users", entity: "app_user", description: "People who can sign in, and what they can do." },
      { slug: "roles", label: "Roles & permissions", permission: "admin.users:manage", description: "What each role can do, which Entra groups grant it, and who has it." },
      { slug: "access-groups", label: "Access groups", permission: "admin.users:manage", description: "Map Entra groups to roles or client scopes." },
      { slug: "access-overview", label: "Access overview", permission: "admin.users:manage", description: "Who can see and change each table, who has each additional privilege and who sees each menu item: by user, role or access group." },
      { slug: "settings", label: "Settings", permission: "admin.settings:manage", description: "Business settings (ex-environment variables)." },
      { slug: "jobs", label: "Jobs", permission: "admin.jobs:run", description: "Schedules, run history and re-runs." },
      { slug: "integrations", label: "Integrations", permission: "admin.settings:manage", description: "Entra, Starling, email and data feeds: set up or not, and how their jobs last ran." },
      { slug: "audit", label: "Audit search", permission: "admin.audit:view", description: "Search field-level change history across every record." },
      { slug: "sync", label: "Sync console", permission: "admin.jobs:run", description: "One-way, incremental sync from Dataverse into local and test environments." },
    ],
  },
];

/** What nav visibility depends on: table Read levels, permission codes and the roles' hidden menu items. */
export type NavAccess = Pick<Me, "tables" | "permissions" | "hidden_nav">;

/** "`<workspace>/<slug>`": the key roles use to hide a menu item. */
export const navKey = (workspace: string, slug: string) => `${workspace}/${slug}`;

/** Can the user open the section at all (table Read, or the permission), ignoring the roles' hidden items? */
export function canOpenSection(section: NavSection, access: NavAccess | undefined): boolean {
  if (section.entity) {
    const read = access?.tables?.[section.entity]?.read;
    return read === "own" || read === "all";
  }
  return !section.permission || hasPermission(access?.permissions, section.permission);
}

/** Shown in the menu: openable and not hidden by every one of the user's roles. */
export function canSeeSection(workspaceId: string, section: NavSection, access: NavAccess | undefined): boolean {
  return canOpenSection(section, access) && !(access?.hidden_nav ?? []).includes(navKey(workspaceId, section.slug));
}

/** Workspaces with only the sections the user can see; a workspace with none is dropped (Home always stays). */
export function visibleWorkspaces(access: NavAccess | undefined): Workspace[] {
  return WORKSPACES.map((ws) => ({ ...ws, sections: ws.sections.filter((s) => canSeeSection(ws.id, s, access)) })).filter(
    (ws) => ws.id === "home" || ws.sections.length > 0,
  );
}

/** First section of a workspace the user can see (for /{workspace} redirects and crumbs); falls back to the first. */
export function firstVisibleSection(ws: Workspace, access: NavAccess | undefined): NavSection | undefined {
  return ws.sections.find((s) => canSeeSection(ws.id, s, access)) ?? ws.sections[0];
}

export interface NavItem {
  /** "`<workspace>/<slug>`". */
  key: string;
  workspace: string;
  workspaceLabel: string;
  slug: string;
  label: string;
  entity?: string;
  permission?: string;
}

/** Every staff menu item, in rail order (the role editor's Navigation tab). */
export const NAV_ITEMS: NavItem[] = WORKSPACES.flatMap((ws) =>
  ws.sections.map((s) => ({
    key: navKey(ws.id, s.slug),
    workspace: ws.id,
    workspaceLabel: ws.label,
    slug: s.slug,
    label: s.label,
    entity: s.entity,
    permission: s.permission,
  })),
);

export function findWorkspace(id: string): Workspace | undefined {
  return WORKSPACES.find((w) => w.id === id);
}

/* ------------------------------------------------------------ Client portal */

export interface PortalSection {
  slug: string;
  label: string;
  icon: LucideIcon;
  description: string;
  permission?: string;
}

export const PORTAL_SECTIONS: PortalSection[] = [
  { slug: "tickets", label: "Tickets", icon: TicketIcon, description: "Raise a support ticket and follow its progress." },
  { slug: "approvals", label: "Approvals", icon: CheckCheckIcon, description: "Tickets waiting for your approval." },
  { slug: "projects", label: "Projects", icon: BriefcaseBusinessIcon, description: "Your projects with Datanomy and their progress." },
  {
    slug: "invoices",
    label: "Invoices",
    icon: FileTextIcon,
    description: "Your invoices and what's still outstanding.",
    permission: "portal.financials:view",
  },
];

export function visiblePortalSections(permissions: readonly string[] | undefined): PortalSection[] {
  return PORTAL_SECTIONS.filter((s) => !s.permission || hasPermission(permissions, s.permission));
}
